import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
	verifyGroupOwnership,
	verifyGroupMembershipPermission,
} from "@/lib/security-utils";
import { sanitizeEmail } from "@/lib/validation-utils";
import {
	getErrorMessage,
	getUserFriendlyErrorMessage,
} from "@/utils/error-utils";
import {
	FetchMenteesResponse,
	FetchMenteesMentee,
} from "@/types/supabase-functions";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/primitives";
import { Users, Mail, UserPlus, ArrowLeft, Trash2 } from "lucide-react";

interface MemberRow {
	id: string; // membership id
	mentee_id: string;
	name: string;
	email: string;
	joined_at: string;
	last_activity?: string | null;
}

export default function GroupMembers() {
	const { groupId } = useParams();
	const navigate = useNavigate();
	const { user } = useAuth();

	const [group, setGroup] = useState<any | null>(null);
	const [members, setMembers] = useState<MemberRow[]>([]);
	const [allMentees, setAllMentees] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);

	// Add mentee dialog state
	const [showAddDialog, setShowAddDialog] = useState(false);
	const [selectedMenteeToAdd, setSelectedMenteeToAdd] = useState("");

	// Invite by email state
	const [inviteEmail, setInviteEmail] = useState("");
	const [inviting, setInviting] = useState(false);

	useEffect(() => {
		if (!groupId) return;
		fetchData();
	}, [groupId]);

	const fetchData = async () => {
		try {
			setLoading(true);
			const { data: groupRow } = await supabase
				.from("groups")
				.select("*")
				.eq("id", groupId)
				.maybeSingle();
			setGroup(groupRow || null);

			// Fetch mentees via edge function (provides name/email/last_activity)
			const { data: menteesResp } =
				await supabase.functions.invoke<FetchMenteesResponse>("fetch-mentees");
			const mentees = (menteesResp?.mentees || []).filter(
				(s) => s.status === "accepted",
			);
			setAllMentees(mentees);

			// Fetch memberships and map to enriched member rows
			const { data: memberships } = await supabase
				.from("group_memberships")
				.select("id, mentee_id, joined_at")
				.eq("group_id", groupId);

			const rows: MemberRow[] = (memberships || []).map((m) => {
				const profile =
					mentees.find((x: FetchMenteesMentee) => x.id === m.mentee_id) ||
					({} as Partial<FetchMenteesMentee>);
				return {
					id: m.id,
					mentee_id: m.mentee_id,
					name: profile.name || "Unknown",
					email: profile.email || "Unknown",
					joined_at: m.joined_at,
					last_activity: profile.last_activity || null,
				};
			});
			setMembers(rows);
		} catch (e) {
			console.error("Error loading group members", e);
			setGroup(null);
			setMembers([]);
		} finally {
			setLoading(false);
		}
	};

	const availableMentees = useMemo(() => {
		const inGroup = new Set(members.map((m) => m.mentee_id));
		return (allMentees || []).filter((m: any) => !inGroup.has(m.id));
	}, [members, allMentees]);

	const removeMember = useCallback(
		async (membershipId: string) => {
			if (!membershipId || !user?.id) return;
			try {
				// Use security utility to verify permission
				const { hasPermission } = await verifyGroupMembershipPermission(
					user.id,
					membershipId,
				);
				if (!hasPermission) {
					alert(
						"Unauthorized: you do not have permission to remove this mentee.",
					);
					return;
				}

				const { error } = await supabase
					.from("group_memberships")
					.delete()
					.eq("id", membershipId);
				if (error) {
					const errorMsg = getUserFriendlyErrorMessage(
						error,
						"removing member",
					);
					alert(errorMsg);
				} else {
					await fetchData();
				}
			} catch (e) {
				console.error(e);
				const errorMsg = getUserFriendlyErrorMessage(e, "removing member");
				alert(errorMsg);
			}
		},
		[user?.id],
	);

	const addMenteeToGroup = useCallback(async () => {
		if (!selectedMenteeToAdd || !groupId || !user?.id) return;

		// Verify group ownership before adding member
		const ownsGroup = await verifyGroupOwnership(user.id, groupId);
		if (!ownsGroup) {
			alert(
				"Unauthorized: You do not have permission to add members to this group.",
			);
			return;
		}

		try {
			const { error } = await supabase.from("group_memberships").insert({
				group_id: groupId,
				mentee_id: selectedMenteeToAdd,
				added_by: user.id,
			});

			if (error) {
				const errorMsg = getUserFriendlyErrorMessage(error, "adding mentee");
				alert(errorMsg);
			} else {
				setSelectedMenteeToAdd("");
				setShowAddDialog(false);
				await fetchData();
			}
		} catch (err) {
			console.error("Error adding mentee to group:", err);
			const errorMsg = getUserFriendlyErrorMessage(err, "adding mentee");
			alert(errorMsg);
		}
	}, [selectedMenteeToAdd, groupId, user?.id]);

	const inviteMenteeByEmail = useCallback(async () => {
		if (!inviteEmail.trim()) return;

		// Validate email format
		const sanitizedEmail = sanitizeEmail(inviteEmail.trim());
		if (!sanitizedEmail) {
			alert("Please enter a valid email address");
			return;
		}

		try {
			setInviting(true);
			const { data, error } = await supabase.functions.invoke("invite-mentee", {
				body: { email: sanitizedEmail },
			});

			if (error) {
				const errorMsg = getUserFriendlyErrorMessage(
					error,
					"sending invitation",
				);
				alert(errorMsg);
			} else if (data?.error) {
				// Handle specific error cases from edge function
				if (data.error === "User not found") {
					alert(
						data.details ||
							"No user found with that email address. They need to create an account first.",
					);
				} else if (data.error === "Relationship exists") {
					alert(
						data.details ||
							"This user is already your mentee or has a pending invitation.",
					);
				} else {
					alert("Error sending invitation: " + (data.details || data.error));
				}
				setInviteEmail("");
			} else {
				alert(data?.message || "Invitation sent successfully");
				setInviteEmail("");
			}
		} catch (e) {
			console.error(e);
			const errorMsg = getUserFriendlyErrorMessage(e, "sending invitation");
			alert(errorMsg);
		} finally {
			setInviting(false);
		}
	}, [inviteEmail]);

	const emailAll = () => {
		const emails = members.map((m) => m.email).filter(Boolean);
		const mailto = `mailto:${encodeURIComponent(emails.join(","))}?subject=${encodeURIComponent("Message from your mentor")}`;
		window.location.href = mailto;
	};

	const emailOne = (email: string) => {
		if (!email) return;
		window.location.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent("Message from your mentor")}`;
	};

	const isActive = (last?: string | null) => {
		if (!last) return false;
		const d = new Date(last);
		if (isNaN(d.getTime())) return false;
		const days = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
		return days <= 14; // active if activity within last 14 days
	};

	if (loading) {
		return (
			<div className="text-center py-8">
				<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
				<p className="text-muted-foreground mt-2">Loading members…</p>
			</div>
		);
	}

	if (!group) {
		return (
			<div className="container mx-auto px-4 py-8 max-w-7xl">
				<p className="text-center text-muted-foreground">Group not found</p>
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-8 max-w-7xl space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center space-x-3">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate(`/mentor/group/${group.id}`)}
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Back to Group
					</Button>
					<div className="h-5 w-px bg-border" />
					<h1 className="text-xl font-semibold flex items-center">
						<Users className="w-5 h-5 mr-2" />
						Manage Members
					</h1>
				</div>
				<div className="flex items-center space-x-2">
					<Button variant="outline" onClick={emailAll}>
						<Mail className="w-4 h-4 mr-2" />
						Email Group
					</Button>
					<Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
						<DialogTrigger asChild>
							<Button>
								<UserPlus className="w-4 h-4 mr-2" />
								Add Mentee
							</Button>
						</DialogTrigger>
						<DialogContent className="sm:max-w-md">
							<DialogHeader>
								<DialogTitle>Add mentee to {group.name}</DialogTitle>
							</DialogHeader>
							<div className="space-y-4">
								<div className="space-y-2">
									<Label>Select existing mentee</Label>
									<Select
										value={selectedMenteeToAdd}
										onValueChange={setSelectedMenteeToAdd}
									>
										<SelectTrigger>
											<SelectValue placeholder="Choose mentee" />
										</SelectTrigger>
										<SelectContent>
											{availableMentees.map((m: any) => (
												<SelectItem key={m.id} value={m.id}>
													{m.name} ({m.email})
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<div className="flex justify-end">
										<Button
											onClick={addMenteeToGroup}
											disabled={!selectedMenteeToAdd}
										>
											Add to Group
										</Button>
									</div>
								</div>
								<div className="border-t pt-4 space-y-2">
									<Label>Invite by email</Label>
									<div className="flex items-center space-x-2">
										<Input
											placeholder="email@example.com"
											value={inviteEmail}
											onChange={(e) => setInviteEmail(e.target.value)}
										/>
										<Button
											onClick={inviteMenteeByEmail}
											disabled={inviting || !inviteEmail.trim()}
										>
											Send Invite
										</Button>
									</div>
									<p className="text-xs text-muted-foreground">
										If they accept the invite, they'll be added as your mentee.
										You can then add them to this group.
									</p>
								</div>
							</div>
						</DialogContent>
					</Dialog>
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Members of {group.name}</CardTitle>
					<CardDescription>
						Manage mentees assigned to this group
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="rounded-md border overflow-auto">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Email</TableHead>
									<TableHead>Active</TableHead>
									<TableHead>Joined</TableHead>
									<TableHead className="text-right">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{members.map((m) => (
									<TableRow key={m.id}>
										<TableCell>{m.name}</TableCell>
										<TableCell>{m.email}</TableCell>
										<TableCell>
											{isActive(m.last_activity) ? (
												<span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">
													Active
												</span>
											) : (
												<span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
													Inactive
												</span>
											)}
										</TableCell>
										<TableCell>
											{new Date(m.joined_at).toLocaleDateString()}
										</TableCell>
										<TableCell className="text-right space-x-2">
											<Button
												size="sm"
												variant="outline"
												onClick={() => emailOne(m.email)}
											>
												<Mail className="w-4 h-4 mr-2" />
												Email
											</Button>
											<Button
												size="sm"
												variant="destructive"
												onClick={() => {
													if (confirm(`Remove ${m.name} from group?`))
														removeMember(m.id);
												}}
											>
												<Trash2 className="w-4 h-4 mr-2" />
												Unassign
											</Button>
										</TableCell>
									</TableRow>
								))}
								{members.length === 0 && (
									<TableRow>
										<TableCell
											colSpan={5}
											className="text-center text-muted-foreground"
										>
											No members in this group
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
